import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Loader2, Sparkles, Gift, Tag, ArrowRight, ShieldCheck, Calendar, Clock, MapPin, Users, Eye, BarChart3, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const usd = (c) => `$${((c || 0) / 100).toFixed(c % 100 === 0 ? 0 : 2)}`;

export default function SponsorSignupPage() {
  const { eventId } = useParams();
  const [cfg, setCfg] = useState(null);
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState([]); // multi-select tier keys
  const [form, setForm] = useState({
    sponsor_name: '', sponsor_email: '', sponsor_phone: '',
    logo_url: '', website_url: '',
  });
  const [offers, setOffers] = useState({}); // map: tier_key -> offer fields

  useEffect(() => {
    (async () => {
      try {
        const [c, evRes] = await Promise.all([
          axios.get(`${API}/sponsorship/event/${eventId}/config`),
          axios.get(`${API}/hosted-events?status=all&limit=200`),
        ]);
        setCfg(c.data);
        setEvent((evRes.data.events || []).find(e => e.event_id === eventId));
      } catch {
        toast.error('Could not load sponsorship details');
      } finally { setLoading(false); }
    })();
  }, [eventId]);

  // Build the unified tier list (defaults + custom)
  const tiers = useMemo(() => {
    if (!cfg) return [];
    const out = [];
    if (cfg.logo_soup_enabled !== false) {
      out.push({
        key: 'logo_soup',
        kind: 'logo_soup',
        name: 'Logo Soup Sponsorship',
        price_cents: cfg.logo_soup_price_cents,
        description: 'Logo + link in the sponsor row on the event page.',
        benefits: ['Logo on event landing page', 'Direct link to your site', 'Listed in event marketing email', '⚡ Activates instantly on payment'],
        includes_swag_bag: false,
        accent: 'emerald',
        icon: Tag,
        instant: true,
      });
    }
    if (cfg.swag_bag_tier_enabled !== false && cfg.swag_bag_enabled) {
      out.push({
        key: 'swag_bag',
        kind: 'swag_bag',
        name: 'Swag Bag Sponsorship',
        price_cents: (cfg.logo_soup_price_cents || 0) + (cfg.swag_bag_offer_price_cents || 0),
        description: 'Logo Soup + a giveaway/discount listing in the digital swag bag.',
        benefits: ['Everything in Logo Soup', 'Featured offer in Digital Swag Bag', 'First-come-first-served claim tracking', 'Reach every attendee on event day'],
        includes_swag_bag: true,
        badge: 'Best',
        accent: 'indigo',
        icon: Gift,
      });
    }
    (cfg.custom_tiers || []).forEach(t => {
      out.push({
        key: t.tier_id,
        kind: 'custom',
        tier_id: t.tier_id,
        name: t.name,
        price_cents: t.price_cents,
        description: t.description,
        benefits: t.benefits || [],
        includes_swag_bag: !!t.includes_swag_bag,
        quantity_total: t.quantity_total || 0,
        quantity_sold: t.quantity_sold || 0,
        quantity_remaining: t.quantity_remaining,
        sold_out: !!t.sold_out,
        accent: 'amber',
        icon: Sparkles,
      });
    });
    return out;
  }, [cfg]);

  useEffect(() => {
    // On config load, default to selecting Logo Soup if available and not sold out
    if (tiers.length > 0 && selectedKeys.length === 0) {
      const ls = tiers.find(t => t.kind === 'logo_soup' && !t.sold_out);
      if (ls) setSelectedKeys([ls.key]);
    }
    // eslint-disable-next-line
  }, [tiers]);

  const toggleTier = (t) => {
    if (t.sold_out) return;
    setSelectedKeys(prev => {
      if (prev.includes(t.key)) return prev.filter(k => k !== t.key);
      // Swag Bag default tier already includes Logo Soup — keep them mutually exclusive
      if (t.kind === 'swag_bag') {
        const next = prev.filter(k => k !== 'logo_soup');
        return [...next, t.key];
      }
      if (t.kind === 'logo_soup') {
        const next = prev.filter(k => k !== 'swag_bag');
        return [...next, t.key];
      }
      return [...prev, t.key];
    });
  };

  const setOfferField = (tierKey, field, value) => {
    setOffers(prev => ({
      ...prev,
      [tierKey]: { ...(prev[tierKey] || { title: '', description: '', type: 'discount', value_cents: '', quantity_total: '', redeem_instructions: '', redeem_link: '', image_url: '' }), [field]: value },
    }));
  };

  const selectedTiers = tiers.filter(t => selectedKeys.includes(t.key));
  const totalCents = selectedTiers.reduce((s, t) => s + (t.price_cents || 0), 0);
  const swagTiers = selectedTiers.filter(t => t.includes_swag_bag);

  const submit = async () => {
    if (!form.sponsor_name || !form.sponsor_email) { toast.error('Name and email required'); return; }
    if (selectedTiers.length === 0) { toast.error('Pick at least one tier'); return; }
    for (const t of swagTiers) {
      const o = offers[t.key] || {};
      if (!o.title || !o.description || !o.quantity_total) {
        toast.error(`Fill in the swag bag offer for "${t.name}"`);
        return;
      }
    }
    setSubmitting(true);
    try {
      const selections = selectedTiers.map(t => {
        const o = offers[t.key];
        return {
          tier: t.kind,
          tier_id: t.kind === 'custom' ? t.tier_id : undefined,
          offer: t.includes_swag_bag && o ? {
            ...o,
            value_cents: Math.round(parseFloat(o.value_cents || '0') * 100),
            quantity_total: parseInt(o.quantity_total, 10),
          } : undefined,
        };
      });
      const res = await axios.post(`${API}/sponsorship/event/${eventId}/checkout`, {
        ...form,
        selections,
      });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start checkout');
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen pt-20 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-indigo-500" /></div>;

  if (!cfg?.enabled) {
    return (
      <div className="min-h-screen pt-20 max-w-md mx-auto p-6 text-center">
        <ShieldCheck className="h-12 w-12 text-muted-foreground/40 mx-auto mb-3" />
        <h1 className="text-xl font-bold">Sponsorship is not currently open</h1>
        <p className="text-sm text-muted-foreground mt-2">{event?.name ? `for ${event.name}` : 'for this event'}.</p>
        <Link to={event ? `/events/${event.city}/${event.slug}` : '/events'}><Button variant="outline" className="mt-4">View event →</Button></Link>
      </div>
    );
  }

  if (tiers.length === 0) {
    return (
      <div className="min-h-screen pt-20 max-w-md mx-auto p-6 text-center">
        <h1 className="text-xl font-bold">No sponsorship tiers configured yet</h1>
        <p className="text-sm text-muted-foreground mt-2">Check back soon.</p>
      </div>
    );
  }

  const mk = cfg.media_kit || {};
  const hasMedia = !!(mk.expected_attendance || mk.estimated_reach || mk.demographics || mk.age_range || mk.social_reach || mk.extra_notes);

  return (
    <div className="min-h-screen bg-background pt-20 pb-20" data-testid="sponsor-signup-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 space-y-6">
        <div>
          <p className="text-xs uppercase tracking-wider text-emerald-500 font-semibold">Become a Sponsor</p>
          <h1 className="text-3xl sm:text-4xl font-bold mt-1">{event?.name || 'Eventism Event'}</h1>
          {event?.tagline && <p className="text-sm text-muted-foreground mt-1">{event.tagline}</p>}
        </div>

        {/* Event info */}
        {event && (
          <Card className="border-border" data-testid="sponsor-event-info">
            <CardContent className="p-4 grid sm:grid-cols-3 gap-3 text-sm">
              <div className="flex items-center gap-2"><Calendar className="h-4 w-4 text-indigo-500" /><span>{event.event_date}</span></div>
              {event.event_time && <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-indigo-500" /><span>{event.event_time}{event.end_time ? ` – ${event.end_time}` : ''}</span></div>}
              {(event.venue_name || event.venue_address) && <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-indigo-500" /><span>{event.venue_name}{event.venue_city ? `, ${event.venue_city}` : ''}</span></div>}
            </CardContent>
          </Card>
        )}

        {/* Media kit */}
        {hasMedia && (
          <Card className="border-indigo-500/30 bg-indigo-500/5" data-testid="sponsor-media-kit">
            <CardContent className="p-5">
              <p className="text-xs uppercase tracking-wider text-indigo-400 font-semibold flex items-center gap-1.5"><BarChart3 className="h-3.5 w-3.5" />Media Kit</p>
              <div className="grid sm:grid-cols-3 gap-4 mt-3">
                {mk.expected_attendance && <div><p className="text-[11px] uppercase text-muted-foreground tracking-wider flex items-center gap-1"><Users className="h-3 w-3" />Attendance</p><p className="text-lg font-semibold">{mk.expected_attendance}</p></div>}
                {mk.estimated_reach && <div><p className="text-[11px] uppercase text-muted-foreground tracking-wider flex items-center gap-1"><Eye className="h-3 w-3" />Estimated reach</p><p className="text-lg font-semibold">{mk.estimated_reach}</p></div>}
                {mk.age_range && <div><p className="text-[11px] uppercase text-muted-foreground tracking-wider">Age range</p><p className="text-lg font-semibold">{mk.age_range}</p></div>}
                {mk.social_reach && <div className="sm:col-span-3"><p className="text-[11px] uppercase text-muted-foreground tracking-wider">Social reach</p><p className="text-sm">{mk.social_reach}</p></div>}
                {mk.demographics && <div className="sm:col-span-3"><p className="text-[11px] uppercase text-muted-foreground tracking-wider">Primary demographics</p><p className="text-sm whitespace-pre-line">{mk.demographics}</p></div>}
                {mk.extra_notes && <div className="sm:col-span-3"><p className="text-[11px] uppercase text-muted-foreground tracking-wider">Past highlights</p><p className="text-sm whitespace-pre-line">{mk.extra_notes}</p></div>}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tiers */}
        <div>
          <h2 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">Choose one or more tiers</h2>
          <p className="text-[11px] text-muted-foreground mb-3">Tip: Logo Soup activates instantly when you pay — pair it with any premium tier.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {tiers.map(t => {
              const Icon = t.icon || Tag;
              const isSel = selectedKeys.includes(t.key);
              const accentMap = {
                emerald: { ring: 'border-emerald-500 ring-2 ring-emerald-500/30 bg-emerald-500/5', icon: 'text-emerald-500', badge: 'bg-emerald-500/10 border-emerald-500/30', check: 'text-emerald-500', box: 'bg-emerald-500 border-emerald-500' },
                indigo: { ring: 'border-indigo-500 ring-2 ring-indigo-500/30 bg-indigo-500/5', icon: 'text-indigo-500', badge: 'bg-indigo-500/10 border-indigo-500/30', check: 'text-indigo-500', box: 'bg-indigo-500 border-indigo-500' },
                amber: { ring: 'border-amber-500 ring-2 ring-amber-500/30 bg-amber-500/5', icon: 'text-amber-500', badge: 'bg-amber-500/10 border-amber-500/30', check: 'text-amber-500', box: 'bg-amber-500 border-amber-500' },
              };
              const a = accentMap[t.accent] || accentMap.emerald;
              const ring = isSel ? a.ring : 'border-border hover:border-muted-foreground/50';
              const soldOut = !!t.sold_out;
              return (
                <button
                  key={t.key}
                  type="button"
                  disabled={soldOut}
                  onClick={() => toggleTier(t)}
                  className={`text-left rounded-xl border p-5 transition flex flex-col relative ${ring} ${soldOut ? 'opacity-50 cursor-not-allowed' : ''}`}
                  data-testid={t.kind === 'custom' ? `tier-custom-${t.tier_id}` : `tier-${t.kind.replace('_', '-')}`}
                >
                  {/* Checkbox indicator */}
                  <div className={`absolute top-3 right-3 w-5 h-5 rounded border-2 flex items-center justify-center transition ${isSel ? a.box : 'border-muted-foreground/40 bg-transparent'}`}>
                    {isSel && <Check className="h-3 w-3 text-white" />}
                  </div>
                  <div className="flex items-center gap-2 mb-1 pr-6">
                    <Icon className={`h-4 w-4 ${a.icon}`} />
                    <p className="font-semibold flex-1">{t.name}</p>
                  </div>
                  <div className="flex items-center gap-1.5 mb-2 flex-wrap">
                    {soldOut && <Badge variant="outline" className="text-[10px] bg-rose-500/10 border-rose-500/30 text-rose-500" data-testid={`tier-sold-out-${t.kind === 'custom' ? t.tier_id : t.kind}`}>Sold out</Badge>}
                    {!soldOut && t.badge && <Badge variant="outline" className={`text-[10px] ${a.badge}`}>{t.badge}</Badge>}
                    {t.instant && <Badge variant="outline" className="text-[10px] bg-amber-500/10 border-amber-500/30 text-amber-500" data-testid="instant-activation-badge">⚡ Instant activation</Badge>}
                  </div>
                  {t.description && <p className="text-xs text-muted-foreground mb-3">{t.description}</p>}
                  <p className="text-2xl font-bold">{usd(t.price_cents)}</p>
                  {t.includes_swag_bag && <p className="text-[11px] text-indigo-400 mt-1 flex items-center gap-1"><Gift className="h-3 w-3" />Swag bag offer included</p>}
                  {!soldOut && t.quantity_total > 0 && (
                    <p className="text-[11px] text-amber-400 mt-1" data-testid={`tier-availability-${t.tier_id || t.kind}`}>{t.quantity_remaining} of {t.quantity_total} available</p>
                  )}
                  {t.benefits && t.benefits.length > 0 && (
                    <ul className="mt-3 space-y-1">
                      {t.benefits.map((b, i) => (
                        <li key={i} className="text-xs flex items-start gap-1.5">
                          <Check className={`h-3 w-3 ${a.check} mt-0.5 flex-shrink-0`} />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Sponsor info */}
        <Card className="border-border">
          <CardContent className="p-5 space-y-3">
            <h2 className="font-semibold">Sponsor info</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Company name *</Label><Input data-testid="sponsor-name" value={form.sponsor_name} onChange={e => setForm({ ...form, sponsor_name: e.target.value })} /></div>
              <div><Label>Email *</Label><Input data-testid="sponsor-email" type="email" value={form.sponsor_email} onChange={e => setForm({ ...form, sponsor_email: e.target.value })} /></div>
              <div><Label>Phone</Label><Input value={form.sponsor_phone} onChange={e => setForm({ ...form, sponsor_phone: e.target.value })} /></div>
              <div><Label>Website</Label><Input data-testid="sponsor-website" placeholder="https://" value={form.website_url} onChange={e => setForm({ ...form, website_url: e.target.value })} /></div>
              <div className="sm:col-span-2"><Label>Logo URL</Label><Input data-testid="sponsor-logo" placeholder="https://yoursite.com/logo.png" value={form.logo_url} onChange={e => setForm({ ...form, logo_url: e.target.value })} /></div>
            </div>
          </CardContent>
        </Card>

        {/* Swag bag offer fields — one per selected swag-includes tier */}
        {swagTiers.map(t => {
          const o = offers[t.key] || { title: '', description: '', type: 'discount', value_cents: '', quantity_total: '', redeem_instructions: '', redeem_link: '', image_url: '' };
          return (
            <Card key={t.key} className="border-indigo-500/30 bg-indigo-500/5" data-testid={`swag-offer-section-${t.kind === 'custom' ? t.tier_id : t.kind}`}>
              <CardContent className="p-5 space-y-3">
                <h2 className="font-semibold flex items-center gap-2"><Gift className="h-4 w-4 text-indigo-500" /> Swag Bag offer for "{t.name}"</h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="sm:col-span-2"><Label>Title *</Label><Input data-testid={`offer-title-${t.kind === 'custom' ? t.tier_id : t.kind}`} placeholder="e.g. 20% off your next stay" value={o.title} onChange={e => setOfferField(t.key, 'title', e.target.value)} /></div>
                  <div>
                    <Label>Type *</Label>
                    <select className="w-full h-9 rounded-md border border-border bg-background px-3 text-sm" value={o.type} onChange={e => setOfferField(t.key, 'type', e.target.value)}>
                      <option value="discount">Discount</option>
                      <option value="giveaway">Giveaway</option>
                    </select>
                  </div>
                  <div><Label>Value (USD)</Label><Input type="number" min="0" step="1" value={o.value_cents} onChange={e => setOfferField(t.key, 'value_cents', e.target.value)} /></div>
                  <div><Label>Quantity *</Label><Input type="number" min="1" value={o.quantity_total} onChange={e => setOfferField(t.key, 'quantity_total', e.target.value)} /></div>
                  <div><Label>Image URL</Label><Input value={o.image_url} onChange={e => setOfferField(t.key, 'image_url', e.target.value)} /></div>
                  <div className="sm:col-span-2"><Label>Description *</Label><Textarea rows={3} value={o.description} onChange={e => setOfferField(t.key, 'description', e.target.value)} /></div>
                  <div className="sm:col-span-2"><Label>Redeem instructions</Label><Textarea rows={2} placeholder="Step-by-step on how to claim" value={o.redeem_instructions} onChange={e => setOfferField(t.key, 'redeem_instructions', e.target.value)} /></div>
                  <div className="sm:col-span-2"><Label>Redeem link</Label><Input placeholder="https://" value={o.redeem_link} onChange={e => setOfferField(t.key, 'redeem_link', e.target.value)} /></div>
                </div>
              </CardContent>
            </Card>
          );
        })}

        {/* Submit */}
        <Card className="border-border bg-muted/30">
          <CardContent className="p-5 flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Total today</p>
              <p className="text-3xl font-bold" data-testid="sponsor-total">{usd(totalCents)}</p>
              <p className="text-[11px] text-muted-foreground mt-1">
                {selectedTiers.length === 0 ? 'Pick a tier to continue' :
                  selectedTiers.length === 1 ? `${selectedTiers[0].name}` :
                    `${selectedTiers.length} tiers selected`} · Secure payment via Stripe
              </p>
            </div>
            <Button onClick={submit} disabled={submitting || selectedTiers.length === 0} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="sponsor-checkout-btn">
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
              Pay & Become a Sponsor <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
